import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import { postData } from '../../services/apiService';
import PasswordStrengthMeter from './PasswordStrengthMeter';

interface ChangeExpiredPasswordProps {
  userId: number;
  message: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const ChangeExpiredPassword: React.FC<ChangeExpiredPasswordProps> = ({
  userId,
  message,
  onSuccess,
  onCancel
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    try {
      setIsLoading(true);
      const response = await postData('/api/auth/change-expired-password', {
        userId,
        currentPassword,
        newPassword
      });

      if (response.status === 'success') {
        setSuccess(response.message);
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        setError(response.message || 'Failed to change password');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        padding: 4,
        position: 'relative'
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 600,
          textAlign: 'center',
          mb: 4
        }}
      >
        <Lock sx={{ fontSize: 64, color: 'warning.main', mb: 3 }} />
        <Typography variant="h3" component="h1" gutterBottom color="primary">
          Password Expired
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
          {message}
        </Typography>
      </Box>

      <Paper
        elevation={6}
        sx={{
          padding: 6,
          width: '100%',
          maxWidth: 600,
          borderRadius: 3
        }}
      >
        <form onSubmit={handleSubmit}>
          <TextField
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoComplete="current-password"
            sx={{ mb: 2, '& .MuiInputBase-root': { height: 56 } }}
          />

          <TextField
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoComplete="new-password"
            sx={{ mb: 1, '& .MuiInputBase-root': { height: 56 } }}
          />

          {newPassword && (
            <Box sx={{ mt: 1, mb: 3 }}>
              <PasswordStrengthMeter 
                password={newPassword} 
                showDetails={true}
                showSuggestions={true}
              />
            </Box>
          )}

          <TextField
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoComplete="new-password"
            error={!!(confirmPassword && newPassword !== confirmPassword)}
            helperText={
              confirmPassword && newPassword !== confirmPassword
                ? 'Passwords do not match'
                : ''
            }
            sx={{ mb: 3, '& .MuiInputBase-root': { height: 56 } }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box sx={{ mt: 4, display: 'flex', gap: 3 }}>
            <Button
              onClick={onCancel}
              variant="outlined"
              size="large"
              fullWidth
              disabled={isLoading}
              sx={{ py: 1.5, fontSize: '1.1rem' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={isLoading || newPassword !== confirmPassword}
              sx={{ py: 1.5, fontSize: '1.1rem' }}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Change Password'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ChangeExpiredPassword;